// Feature 4 — the curtain (F4 §5): Mom's prepared plan for the user-day,
// revealed in its entirety only when the user chooses. Entries group by the
// goal they serve for explainability, never fairness (§36); each carries
// its policy reason (§37). Skipping is day-scoped, never rejection (§29).

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, Map as MapIcon, RefreshCw, Sparkles, X } from 'lucide-react'
import { Button } from '../ui/Button'
import {
  adoptEntry,
  getPreparedDay,
  rebuildPreparedDay,
  revealPreparedDay,
  skipEntry,
} from '../../services/preparedDayService'
// Type-only: verbatimModuleSyntax keeps these in the emitted import,
// and the dev server then 500s on a missing runtime export.
import type { PreparedDay, PreparedEntry } from '../../services/preparedDayService'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'
import { AskEyebrow } from '../today/AskEyebrow'

const REASON_LABEL: Record<string, string> = {
  hard_commitment: 'deadline soon',
  user_committed: 'you said this matters right now',
  recent_momentum: "you're on a roll here",
  priority: 'important to you',
  deadline: 'has a date',
  anti_starvation: "hasn't had attention lately",
  backlog_resurface: 'from your backlog — still worth a look?',
  structurally_thin_focus_branch: 'needs a map',
  parked_wake_up_candidate: 'resting — wake it?',
  dream_needs_goal: 'dream without a goal yet',
}

export function PreparedDayCurtain(
  { onAdopted, alreadyOnThePlate = [] }:
  { onAdopted?: () => void; alreadyOnThePlate?: number[] },
) {
  const navigate = useNavigate()
  const [day, setDay] = useState<PreparedDay | null>(null)
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [replanning, setReplanning] = useState(false)

  const load = useCallback(async () => {
    try { setDay(await getPreparedDay()) } catch { setDay(null) }
  }, [])
  useEffect(() => { void load() }, [load])

  if (!day?.exists || !day.entries) return null
  // An option is only an OPTION if it is not already today's plan.
  //
  // The prepared day and the focus list draw from the same pool, so this
  // offered the very task "Start here" was naming — "Why this: Next in line ·
  // [Start this]" above, "hasn't had attention lately · [Adopt]" below, for one
  // task, with two verbs and two contradicting reasons (owner, 2026-09-08:
  // *"These are again mixed signals."*). Anything already on the plate is the
  // plan, not an extra.
  const onThePlate = new Set(alreadyOnThePlate)
  const live = day.entries.filter(
    (e) => !['CONSUMED', 'STALE', 'ADOPTED', 'SKIPPED_FOR_DAY'].includes(e.day_state)
      && !(e.canonical_item_id != null && onThePlate.has(e.canonical_item_id))
  )
  if (live.length === 0) return null

  const reveal = async () => {
    setOpen(true)
    try { await revealPreparedDay() } catch { /* presentation is best-effort */ }
  }

  const handleAdopt = async (e: PreparedEntry) => {
    setBusyId(e.id)
    try {
      const r = await adoptEntry(e.id)
      if (r.next_step === 'open_journey_flow' && r.item_id) {
        // ?journey=start: the detail page auto-opens the AI mapping preview —
        // adopting an invitation must START the expansion, not park the user
        // on a static page (owner report 2026-08-25).
        navigate(r.item_type === 'skill' ? `/skills/${r.item_id}?journey=start` : `/goals/${r.item_id}?journey=start`)
      } else if (r.next_step === 'open_chat') {
        navigate('/horton-ai')
      } else {
        onAdopted?.()
      }
      await load()
    } finally { setBusyId(null) }
  }

  const handleSkip = async (e: PreparedEntry) => {
    setBusyId(e.id)
    try { await skipEntry(e.id); await load() } finally { setBusyId(null) }
  }

  const groups = new Map<string, PreparedEntry[]>()
  for (const e of live) {
    const key = e.group_title || 'On its own'
    groups.set(key, [...(groups.get(key) || []), e])
  }
  const coldStart = day.plan?.mode === 'cold_start'

  return (
    <div className="mb-3 p-4 rounded-2xl border border-brand-subtle-border bg-surface shadow-sm">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : void reveal())}
        className="w-full flex items-center gap-2 text-left"
      >
        <span className="flex-1 min-w-0">
          <AskEyebrow icon={<Sparkles />} label={ORI_KIND_LABEL.offer}
                      typeWord={coldStart ? null : `${live.length} option${live.length === 1 ? '' : 's'} ready for today`} />
          {coldStart && (
            <span className="mt-0.5 block text-sm font-semibold text-primary">Your map is resting — choose what to wake up</span>
          )}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-muted" /> : <ChevronRight className="w-4 h-4 text-muted" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-secondary">
              These are prepared options, not obligations — adopt what fits, skip what doesn't. Skipping only rests it for today.
            </p>
            <button
              type="button"
              title="Replan with what's on your map now (added something new? plans changed?)"
              disabled={replanning}
              onClick={() => {
                setReplanning(true)
                rebuildPreparedDay()
                  .then((d) => setDay(d))
                  .catch(() => undefined)
                  .finally(() => setReplanning(false))
              }}
              className="p-1 text-muted hover:text-secondary shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${replanning ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {[...groups.entries()].map(([group, entries]) => (
            <div key={group}>
              <div className="text-app-micro font-medium text-brand uppercase tracking-wide mb-1">{group}</div>
              <div className="space-y-2">
                {entries.map((e) => (
                  <div
                    key={e.id}
                    className="group flex items-start gap-2 p-2 rounded-lg bg-surface cursor-pointer border border-transparent hover:border-brand/40 hover:shadow-sm transition-all"
                    title="Click to open this item"
                    onClick={(ev) => {
                      if ((ev.target as HTMLElement).closest('button,a')) return
                      if (e.entry_role === 'ACTION' && e.canonical_item_id) {
                        navigate(`/tasks/${e.canonical_item_id}`)
                      } else if (e.entry_role === 'STRUCTURE_INVITATION' && e.source_item_id) {
                        navigate(e.source_item_type === 'skill' ? `/skills/${e.source_item_id}` : `/goals/${e.source_item_id}`)
                      } else if (e.entry_role === 'CONVERSATION_INVITATION') {
                        navigate('/horton-ai')
                      }
                    }}
                  >
                    {e.entry_role !== 'ACTION' && <MapIcon className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary leading-snug" style={{ fontFamily: 'var(--font-display)' }}>{e.display_title}</p>
                      <span className="text-app-micro text-muted">
                        {REASON_LABEL[e.reason_code] || e.reason_code}
                        {e.placement ? ` · ${e.placement}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        disabled={busyId === e.id}
                        onClick={() => void handleAdopt(e)}
                        className="bg-brand hover:bg-brand/80 text-white"
                      >
                        {e.entry_role === 'STRUCTURE_INVITATION' ? 'Map it'
                          : e.entry_role === 'CONVERSATION_INVITATION' ? 'Talk it out'
                          : e.reason_code === 'parked_wake_up_candidate' ? 'Wake up' : 'Adopt'}
                      </Button>
                      <button
                        type="button"
                        title="Not today (this is not a rejection)"
                        disabled={busyId === e.id}
                        onClick={() => void handleSkip(e)}
                        className="p-1 text-muted hover:text-secondary"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
